import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Moment from "react-moment";
import "moment/locale/pt-br";
import "./style.css";
import axios from "axios";
import { FeedPostMenu } from "../shared";
import EditPostPopup from "../../editPostPopup";

export default function CrowdfundingFeed() {
  const { user } = useSelector((state) => ({ ...state }));
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [editingPost, setEditingPost] = useState(null);

  const handleEditPost = (post) => {
    setSelectedCampaign(null);
    setEditingPost(post);
  };
  const handlePostUpdated = (updatedPost) => {
    setPosts(prev => prev.map(p => p._id === updatedPost._id ? updatedPost : p));
    setEditingPost(null);
  };
  const handlePostDeleted = (postId) => {
    setPosts(prev => prev.filter(p => p._id !== postId));
    setSelectedCampaign(null);
  };

  const categories = [
    { id: "all", name: "Todas" },
    { id: "creative", name: "Criativo" },
    { id: "technology", name: "Tecnologia" },
    { id: "social", name: "Social" },
    { id: "business", name: "Negócios" },
    { id: "health", name: "Saúde" },
    { id: "education", name: "Educação" },
    { id: "emergency", name: "Emergência" },
  ];

  const fetchPosts = async (pageNum = 1) => {
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/feed/crowdfunding?page=${pageNum}&limit=12`;
      if (category !== "all") url += `&category=${category}`;

      const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${user.token}` } });
      if (pageNum === 1) setPosts(data.posts || []);
      else setPosts((prev) => [...prev, ...(data.posts || [])]);
      setHasMore((data.pagination?.page || 1) < (data.pagination?.pages || 1));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching crowdfunding:", error);
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); fetchPosts(1); }, [category]);

  const getProgressPercent = (raised, goal) => {
    if (!goal || goal === 0) return 0;
    return Math.min(100, Math.round((raised / goal) * 100));
  };

  const getDaysLeft = (deadline) => {
    if (!deadline) return null;
    const now = new Date();
    const end = new Date(deadline);
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  if (loading && posts.length === 0) {
    return <div className="crowdfunding_feed_loading"><div className="crowdfunding_loader"></div><span>Carregando campanhas...</span></div>;
  }

  return (
    <div className="crowdfunding_feed">
      {categories.length > 0 && (
        <div className="simple_filter_bar">
          <div className="filter_buttons">
            {categories.map((filter) => (
              <button
                key={filter.id}
                className={`filter_btn ${category === filter.id ? "active" : ""}`}
                onClick={() => setCategory(filter.id)}
              >
                {filter.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {posts.length === 0 ? (
        <div className="crowdfunding_feed_empty">
          <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
            <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
          </svg>
          <h3>Nenhuma campanha encontrada</h3>
          <p>Crie sua campanha!</p>
        </div>
      ) : (
        <div className="crowdfunding_grid">
          {posts.map((post) => {
            const percent = getProgressPercent(post.crowdfundingData?.raised, post.crowdfundingData?.goal);
            const daysLeft = getDaysLeft(post.crowdfundingData?.deadline);
            return (
              <div key={post._id} className="crowdfunding_card" onClick={() => setSelectedCampaign(post)}>
                <div className="crowdfunding_image">
                  <img src={post.crowdfundingData?.coverImage || post.images?.[0]?.url || post.images?.[0] || "/images/default-crowdfunding.png"} alt="" />
                  <span className="crowdfunding_category_badge">
                    {categories.find(c => c.id === post.crowdfundingData?.category)?.name || "Projeto"}
                  </span>
                </div>
                <div className="crowdfunding_card_content">
                  <h3>{post.crowdfundingData?.title || post.text}</h3>
                  <p className="crowdfunding_story">{post.crowdfundingData?.shortDescription}</p>

                  <div className="crowdfunding_progress">
                    <div className="crowdfunding_progress_bar">
                      <div className="crowdfunding_progress_fill" style={{ width: `${percent}%` }}></div>
                    </div>
                    <div className="crowdfunding_progress_info">
                      <span className="crowdfunding_raised">
                        R$ {(post.crowdfundingData?.raised || 0).toLocaleString("pt-BR")}
                      </span>
                      <span className="crowdfunding_percent">{percent}%</span>
                    </div>
                    <div className="crowdfunding_goal">
                      Meta: R$ {(post.crowdfundingData?.goal || 0).toLocaleString("pt-BR")}
                    </div>
                  </div>

                  <div className="crowdfunding_footer">
                    <span className="crowdfunding_backers">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                        <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                      </svg>
                      {post.crowdfundingData?.backers || 0} apoiadores
                    </span>
                    {daysLeft !== null && (
                      <span className={`crowdfunding_days ${daysLeft === 0 ? "ended" : ""}`}>
                        {daysLeft === 0 ? "Encerrada" : `${daysLeft} dias restantes`}
                      </span>
                    )}
                  </div>
                  <div onClick={(e) => e.stopPropagation()}>
                    <FeedPostMenu
                      post={post}
                      onEdit={handleEditPost}
                      onPostDeleted={handlePostDeleted}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hasMore && (
        <div className="crowdfunding_load_more">
          <button onClick={() => { setPage(p => p + 1); fetchPosts(page + 1); }} disabled={loading}>
            {loading ? "Carregando..." : "Ver mais"}
          </button>
        </div>
      )}

      {editingPost && (
        <EditPostPopup
          post={editingPost}
          user={user}
          setVisible={(visible) => !visible && setEditingPost(null)}
          onPostUpdated={handlePostUpdated}
        />
      )}

      {selectedCampaign && (
        <div className="crowdfunding_modal_overlay" onClick={() => setSelectedCampaign(null)}>
          <div className="crowdfunding_modal" onClick={(e) => e.stopPropagation()}>
            <button className="crowdfunding_modal_close" onClick={() => setSelectedCampaign(null)}>✕</button>

            <div className="crowdfunding_modal_header">
              <img src={selectedCampaign.crowdfundingData?.coverImage || selectedCampaign.images?.[0]} alt="" />
            </div>

            <div className="crowdfunding_modal_body">
              <span className="crowdfunding_modal_category">
                {categories.find(c => c.id === selectedCampaign.crowdfundingData?.category)?.name}
              </span>
              <h2>{selectedCampaign.crowdfundingData?.title}</h2>

              <div className="crowdfunding_modal_progress">
                <div className="crowdfunding_modal_progress_bar">
                  <div
                    className="crowdfunding_modal_progress_fill"
                    style={{ width: `${getProgressPercent(selectedCampaign.crowdfundingData?.raised, selectedCampaign.crowdfundingData?.goal)}%` }}
                  ></div>
                </div>
                <div className="crowdfunding_modal_stats">
                  <div className="crowdfunding_stat">
                    <span className="crowdfunding_stat_value">
                      R$ {(selectedCampaign.crowdfundingData?.raised || 0).toLocaleString("pt-BR")}
                    </span>
                    <span className="crowdfunding_stat_label">arrecadados</span>
                  </div>
                  <div className="crowdfunding_stat">
                    <span className="crowdfunding_stat_value">{selectedCampaign.crowdfundingData?.backers || 0}</span>
                    <span className="crowdfunding_stat_label">apoiadores</span>
                  </div>
                  <div className="crowdfunding_stat">
                    <span className="crowdfunding_stat_value">{getDaysLeft(selectedCampaign.crowdfundingData?.deadline) || 0}</span>
                    <span className="crowdfunding_stat_label">dias restantes</span>
                  </div>
                </div>
                <div className="crowdfunding_modal_goal">
                  Meta: R$ {(selectedCampaign.crowdfundingData?.goal || 0).toLocaleString("pt-BR")}
                </div>
              </div>

              <div className="crowdfunding_modal_story">
                <h4>Sobre o projeto</h4>
                <p>{selectedCampaign.crowdfundingData?.story || selectedCampaign.text}</p>
              </div>

              {selectedCampaign.crowdfundingData?.rewards?.length > 0 && (
                <div className="crowdfunding_modal_rewards">
                  <h4>Recompensas</h4>
                  <div className="crowdfunding_rewards_list">
                    {selectedCampaign.crowdfundingData.rewards.map((reward, i) => (
                      <div key={i} className="crowdfunding_reward_item">
                        <div className="crowdfunding_reward_header">
                          <span className="crowdfunding_reward_value">R$ {reward.value}</span>
                          <span className="crowdfunding_reward_claimed">{reward.claimed || 0} de {reward.limit || "∞"}</span>
                        </div>
                        <h5>{reward.title}</h5>
                        <p>{reward.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedCampaign.crowdfundingData?.updates?.length > 0 && (
                <div className="crowdfunding_modal_updates">
                  <h4>Atualizações</h4>
                  <div className="crowdfunding_updates_list">
                    {selectedCampaign.crowdfundingData.updates.slice(0, 3).map((update, i) => (
                      <div key={i} className="crowdfunding_update_item">
                        <span className="crowdfunding_update_date">
                          <Moment format="DD/MM/YYYY">{update.date}</Moment>
                        </span>
                        <p>{update.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Link to={`/profile/${selectedCampaign.user.username}`} className="crowdfunding_creator">
                <img src={selectedCampaign.user.picture} alt="" />
                <div>
                  <span className="crowdfunding_creator_label">Criador</span>
                  <span className="crowdfunding_creator_name">{selectedCampaign.user.first_name} {selectedCampaign.user.last_name}</span>
                </div>
              </Link>

              <button className="crowdfunding_support_btn">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
                Apoiar este projeto
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
